import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { z } from 'zod';
import { baseQueryArgs, baseExtraOptions, rsosApi } from '../rsosApi';

const DirectoryEntrySchema = z.object({
  identifier: z.string(),
  name: z.string(),
  type: z.enum(['conference', 'transfer']),
});
const DirectoryResponseSchema = z.array(DirectoryEntrySchema);

/**
 * Mirrors the pydantic ``TransferState`` model in
 * ``rsos/services/data/text_to_911/tcapi/models.py``. The backend uses
 * ``DualCaseModel`` so the wire payload includes both snake_case and
 * camelCase keys; we read snake_case to stay consistent with the rest of
 * the TCAPI session payload and with the canonical pydantic form.
 */
const TCAPISessionTransferSchema = z.object({
  status: z.enum(['pending', 'accepted', 'timed_out', 'declined']),
  initiating_authority_account_id: z.string(),
  initiating_authority_display_name: z.string().nullable(),
  receiving_authority_account_id: z.string().nullable(),
  receiving_authority_display_name: z.string().nullable(),
});

/**
 * Mirrors the pydantic ``SessionResponse`` model in
 * ``rsos/services/data/text_to_911/tcapi/models.py``. Top-level fields use
 * snake_case (the canonical pydantic form, which SOUP-1191 standardized
 * the frontend on); the dual-case backend also emits camelCase variants
 * which ``z.object`` strips by default.
 */
const TCAPISessionResponseSchema = z.object({
  server_time_millis: z.number(),
  conversation_id: z.string(),
  authority_account_id: z.string(),
  authority_display_name: z.string(),
  twilio_phone: z.string().nullable(),
  caller_id: z.string(),
  creation_time: z.number().nullable(),
  last_updated_time: z.number().nullable(),
  translation_method: z.string().nullable(),
  last_caller_language: z.string().nullable(),
  override_caller_language: z.string().nullable(),
  outbound_translation: z.boolean(),
  status: z.enum(['pending', 'active', 'inactive', 'ended', 'timed_out']),
  session_type: z.enum([
    'sms',
    'convey',
    'rcs',
    'sms_admin',
    'tcapi',
    'interagency',
  ]),
  accept_status: z
    .enum(['pending', 'accepted', 'timed_out', 'declined'])
    .nullable()
    .optional(),
  expire_time: z.number().nullable(),
  last_accept_time: z.number().nullable(),
  session_id: z.string(),
  transfer: TCAPISessionTransferSchema.nullable(),
  agency_participants: z.array(z.string()).default([]),
});

export type TCAPISessionTransfer = z.infer<typeof TCAPISessionTransferSchema>;
export type TCAPISessionResponse = z.infer<typeof TCAPISessionResponseSchema>;

interface GetTCAPIConversationMetadataArgs {
  /** Caller's phone number (digits only — the leading ``+`` is added by the query). */
  callerID: string;
  /** Name of the authority the user belongs to. */
  orgName: string;
}

const getTCAPIBaseEndpoint = (orgName: string) => {
  return `v1/text_to_911/tcapi/psap/${orgName}`;
};

export const THIRTY_DAYS = 1000 * 60 * 60 * 24 * 30;
export const TWO_HOURS = 1000 * 60 * 60 * 2;

/**
 * Extends the rsosApi for TCAPI endpoints.
 * Authorization header must be manually included to tell RTK which token to use
 * otherwise it will use the access token
 */
export const tcapiAPI = rsosApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * Fetches a phone number's ongoing conversation metadata.
     * @param callerID - phone number
     * @param orgName - Name of authority the user belongs to
     */
    getTCAPIConversationMetadata: builder.query({
      keepUnusedDataFor: Infinity,
      query: ({ callerID, orgName }: GetTCAPIConversationMetadataArgs) => ({
        ...baseQueryArgs,
        url: `${getTCAPIBaseEndpoint(orgName)}/session/+${callerID}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        TCAPISessionResponseSchema.parse(response),
      providesTags: (_result, _error, { callerID }) => [
        { type: 'TCAPIMetaData', id: callerID },
      ],
      // 404 = "no active session" — normal state for any SMS call that
      // hasn't been opened as a TCAPI session yet. Tag the error so
      // rtkQueryErrorMiddleware skips the unhandled-error toast/log; the
      // consumer still receives the rejection and can branch on it.
      transformErrorResponse: (error: FetchBaseQueryError) =>
        typeof error.status === 'number' && error.status === 404
          ? { ...error, isExpectedError: true }
          : error,
      extraOptions: {
        ...baseExtraOptions,
        maxRetries: 0, // don't retry - 404 means no active session. Revisit if 500s become an issue
      },
    }),
    /**
     * Ends a conversation with the given phone number
     * @param callerID - phone number
     * @param orgName - Name of authority the user belongs to
     */
    endTCAPIConversation: builder.mutation({
      query: ({ callerID, orgName }) => ({
        ...baseQueryArgs,
        url: `${getTCAPIBaseEndpoint(orgName)}/session/+${callerID}`,
        method: 'PUT',
        body: { status: 'inactive' },
      }),
      invalidatesTags: (_result, _error, { callerID }) => [
        { type: 'TCAPIMetaData', id: callerID },
      ],
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * Starts a conversation with the given phone number. The conversations
     * slice owns the per-caller ``createConversation`` loading flag via
     * extraReducer matchers on this mutation's pending/fulfilled/rejected
     * lifecycle.
     */
    createTCAPIConversation: builder.mutation({
      query: ({ callerID, orgName }) => ({
        ...baseQueryArgs,
        url: `${getTCAPIBaseEndpoint(orgName)}/session/+${callerID}`,
        method: 'PUT',
        body: { accept_status: 'accepted' },
      }),
      invalidatesTags: (_result, _error, { callerID }) => [
        { type: 'TCAPIMetaData', id: callerID },
      ],
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * Fetches the agency directory for transfer/conference.
     * @param orgName - Name of authority the user belongs to
     */
    getTCAPIAgencyDirectory: builder.query({
      query: ({ orgName }: { orgName: string }) => ({
        ...baseQueryArgs,
        url: `v1/text_to_911/tcapi/agencies/${orgName}/directory`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => {
        const entries = DirectoryResponseSchema.parse(response);
        const filtered = entries.filter(entry => entry.type === 'conference');
        if (filtered.length === 0 && entries.length > 0) {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console -- Warn if all entries were filtered out to help developers. Since may not be an error in prod, we only log for developers.
            console.error(
              'All agencies in TCAPI directory response were filtered out.',
              entries,
            );
          }
        }
        return filtered;
      },
      providesTags: ['TCAPIAgencyDirectory'],
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * Initiates a soft transfer to another agency.
     * @param callerID - phone number
     * @param orgName - Name of authority the user belongs to
     * @param receiving_agency_identifier - identifier of the target agency
     * @param receiving_agency_name - display name of the target agency
     */
    transferTCAPIConversation: builder.mutation({
      query: ({
        callerID,
        orgName,
        receiving_agency_identifier,
        receiving_agency_name,
      }) => ({
        ...baseQueryArgs,
        url: `${getTCAPIBaseEndpoint(orgName)}/session/+${callerID}`,
        method: 'PATCH',
        body: { receiving_agency_identifier, receiving_agency_name },
      }),
      invalidatesTags: (_result, _error, { callerID }) => [
        { type: 'TCAPIMetaData', id: callerID },
      ],
      extraOptions: {
        ...baseExtraOptions,
        // Disable automatic retries — a failed transfer should surface the
        // error to the user immediately rather than silently retrying.
        maxRetries: 0,
      },
    }),
  }),
  overrideExisting: 'throw',
});

export const {
  useGetTCAPIConversationMetadataQuery,
  useLazyGetTCAPIConversationMetadataQuery,
  useEndTCAPIConversationMutation,
  useCreateTCAPIConversationMutation,
  useGetTCAPIAgencyDirectoryQuery,
  useTransferTCAPIConversationMutation,
} = tcapiAPI;

/**
 * Action creator that invalidates the cached TCAPI session metadata for a
 * given caller, forcing the active ``getTCAPIConversationMetadata`` query
 * to re-fetch. Returned action must be dispatched.
 */
export function invalidateTCAPIConversationMetadata(callerID: string) {
  return tcapiAPI.util.invalidateTags([
    { type: 'TCAPIMetaData', id: callerID },
  ]);
}
